import { api } from './client';
import { i18nMap } from '@/lib/i18n-map';

export type StudyKind = 'intraoral' | 'panoramic' | 'cbct' | 'stl' | 'photo';
export type StudyStatus = 'new' | 'processing' | 'ready' | 'failed';
export type FindingSeverity = 'mild' | 'moderate' | 'severe';

export interface StudySummary {
  id: string;
  patientId: string;
  kind: StudyKind;
  studyDate: string;
  device: string | null;
  doctorId: string | null;
  doctorName: string | null;
  visitId: string | null;
  visitDate: string | null;
  /** Rândul de plan pentru care s-a făcut — „CBCT maxilar" —, luat din programare. */
  phaseId: string | null;
  phaseName: string | null;
  note: string | null;
  status: StudyStatus;
  error: string | null;
  fileCount: number;
  bytes: number;
  nameMatches: boolean | null;
  dicomPatientName: string | null;
  findingCount: number;
  /** Dinții scoși din regiunea raportului. */
  excludedTeeth?: number;
  /** Dinții pe care îi arată filmul: cei scriși pe studiu și cei din constatări. */
  teeth: number[];
  /** Expunerea, din tag-urile DICOM. `null` = aparatul n-a scris-o. */
  kvp: number | null;
  mas: number | null;
  exposureMs: number | null;
  dap: number | null;
  signedVersion: number | null;
  signedAt: string | null;
  /**
   * Când s-au șters originalele DICOM după politica clinicii. Volumul pregătit
   * rămâne; fișierele nu se mai descarcă.
   */
  originalsPurgedAt: string | null;
  /** Miniatura seriei celei mai mari, ca link scurt. */
  previewUrl: string | null;
  createdAt: string;
}

export type Vec3 = [number, number, number];

/** Volumul întreg, cum îl trimite serverul: niveluri, bucăți, spațiu în mm. */
export interface VolumeManifestView {
  format: 'DCV2';
  dtype: 'int16';
  endian: 'little';
  modality: string | null;
  spacingKnown: boolean;
  /** Centrul primului voxel, în mm, în spațiul pacientului. */
  origin: Vec3;
  /** Direcția rândurilor, a coloanelor și normala. */
  direction: [number, number, number, number, number, number, number, number, number];
  min: number;
  max: number;
  window: { center: number; width: number };
  levels: Array<{
    scale: number;
    dims: Vec3;
    spacing: Vec3;
    chunks: Array<{ z: number; slices: number; bytes: number; url: string }>;
  }>;
}

export type SceneObjectType =
  | 'measurement'
  | 'arch'
  | 'nerve'
  | 'implant'
  | 'segment'
  | 'registration'
  /** Reperele cefalometrice ale analizei ortodontice. */
  | 'ceph';

export interface SceneObject {
  id: string;
  studyId: string;
  type: SceneObjectType;
  tooth: number | null;
  label: string | null;
  geometry: Record<string, unknown>;
  props: Record<string, unknown>;
  createdBy: string | null;
  createdAt: string;
  updatedBy: string | null;
  updatedAt: string;
}

export interface SceneObjectInput {
  type: SceneObjectType;
  tooth?: number | null;
  label?: string | null;
  geometry: Record<string, unknown>;
  props?: Record<string, unknown>;
}

export interface StudySeriesView {
  id: string;
  description: string | null;
  modality: string | null;
  slices: number;
  pxRows: number | null;
  pxCols: number | null;
  pixelSpacing: number | null;
  sliceThickness: number | null;
  /** Cum era codat fișierul — primul lucru de știut când ceva nu merge. */
  transferSyntax: string | null;
  /** UID-ul DICOM al seriei. */
  seriesUid?: string | null;
  hasVolume: boolean;
  /** Volumul întreg pe 16 biți există: se deschide vizualizatorul 3D. */
  hasFullVolume: boolean;
  /** Dinții de pe film, la o serie de o felie. */
  teeth: number[];
  volume: {
    width: number | null;
    height: number | null;
    depth: number | null;
    windowCenter: number | null;
    windowWidth: number | null;
  } | null;
}

export interface StudyFileView {
  id: string;
  name: string;
  contentType: string | null;
  bytes: number;
  seriesId: string | null;
  /** Dinții de pe film. */
  teeth: number[];
  viewable: boolean;
  /** De ce fișierul nu se arată și nu intră în volum: un DICOM fără imagine, un TIFF necitibil. */
  skipReason: string | null;
}

export interface FindingView {
  id: string;
  tooth: number | null;
  code: string;
  label: string;
  severity: FindingSeverity | null;
  confidence: number | null;
  source: 'doctor' | 'ai';
  note: string | null;
  /** Precizările din catalog: `{ surfaces: ['M','O'] }`, `{ count: '3' }`. */
  attributes: FindingAttributes;
  /** Unde se vede pe o imagine 2D; null = nedesenată. */
  region: FindingRegion | null;
}

/**
 * Locul constatării pe o imagine 2D, în 0…1 ale imaginii (înainte de rotire):
 * caseta `[u, v, lățime, înălțime]` și masca, poligoane închise.
 */
export interface FindingRegion {
  fileId?: string;
  seriesId?: string;
  box?: [number, number, number, number];
  mask?: Array<Array<[number, number]>>;
}

/** O riglă a nivelului osos: joncțiunea smalț-cement → creasta, în 0…1 ale imaginii. */
export interface BoneLevel {
  fileId?: string;
  seriesId?: string;
  a: [number, number];
  b: [number, number];
  /** Lungimea la măsurare; null când imaginea n-are scară. */
  mm: number | null;
  /** Scara e a proiecției, nemăsurată pe obiect. */
  approx: boolean;
}

export type FindingAttributes = Record<string, string | string[]>;

/** Un marcaj pe o felie a dintelui, în milimetrii feliei. */
export interface SliceMark {
  kind: 'arrow' | 'line' | 'angle';
  points: Array<[number, number]>;
}

/** O felie a dintelui: orientarea și deplasarea față de centrul lui, în mm. */
export interface ToothSliceRef {
  o: 'axial' | 'vo' | 'md';
  d: number;
  marks?: SliceMark[];
}

/** Comentariul și aprobarea unui dinte în raport. */
export interface ToothReviewView {
  tooth: number;
  comment: string | null;
  /** Scos din raport: fără card, netipărit. */
  excluded: boolean;
  /** Feliile alese pentru card; null = cele șase implicite. */
  slices: ToothSliceRef[] | null;
  /** Riglele nivelului osos; null = niciuna. */
  boneLevels: BoneLevel[] | null;
  approvedAt: string | null;
  approvedName: string | null;
}

export interface ReportSnapshotItem {
  tooth: number | null;
  code: string;
  label: string;
  severity: FindingSeverity | null;
  note: string | null;
  /** Precizările (suprafețe, număr), cum erau la semnare. */
  attributes?: FindingAttributes;
  region?: FindingRegion | null;
}

/** Antetul și semnatarul tipăriturii: clinica, medicul, ștampilele. */
export interface PrintContext {
  patient: { dob: string | null };
  clinic: {
    name: string | null;
    address: string | null;
    phone: string | null;
    email: string | null;
    logoUrl: string | null;
    stampUrl: string | null;
  };
  signer: {
    name: string;
    title: string | null;
    signatureUrl: string | null;
    stampUrl: string | null;
  } | null;
}

export interface ReportImage {
  id: string;
  path: string;
  label: string;
  width: number;
  height: number;
  /** Dintele pe al cărui card stă imaginea. */
  tooth?: number;
}

/** Ce se trimite la salvarea sau semnarea raportului. */
export interface ReportBody {
  summary?: string | null;
  recommendation?: string | null;
  sections?: Partial<Record<string, string | null>>;
}

export interface ReportView {
  id: string;
  version: number;
  summary: string | null;
  recommendation: string | null;
  /** Concluzia extinsă, pe secțiuni (numai cele scrise). */
  sections?: Partial<Record<string, string>>;
  findings: ReportSnapshotItem[];
  /** Comentariile de pe dinți la semnare. */
  teeth: Array<{
    tooth: number;
    comment: string | null;
    excluded?: boolean;
    slices?: ToothSliceRef[];
    boneLevels?: BoneLevel[];
  }>;
  images: ReportImage[];
  archivedDocumentId: string | null;
  signedAt: string | null;
  signedName: string | null;
}

export interface StudyAnnotation {
  id: string;
  /** `model`: pe o scanare de suprafață, cu puncte în spațiu și fișierul în `fileId`. */
  plane: 'axial' | 'coronal' | 'sagittal' | 'image' | 'model';
  index: number;
  /** `calibration`: o lungime cunoscută, în milimetri în `value`, care dă scara imaginii. */
  kind: 'length' | 'angle' | 'pin' | 'calibration';
  points: Array<{ x: number; y: number; z?: number }>;
  /** Pe o imagine sau pe un model 3D: fișierul pe care stă. */
  fileId?: string | null;
  /** Pe o felie: seria pe care stă. Adnotările de dinainte n-o au. */
  seriesId?: string | null;
  value: number | null;
  text: string | null;
  createdBy: string | null;
  createdAt: string;
}

export interface StudyDetail extends StudySummary {
  patientName: string | null;
  modality: '2d' | '3d' | null;
  annotations: StudyAnnotation[];
  series: StudySeriesView[];
  files: StudyFileView[];
  findings: FindingView[];
  teethReviews: ToothReviewView[];
  reports: ReportView[];
}

export interface FindingMeta {
  code: string;
  label: string;
  category: string;
  /** `any` merge cu sau fără dinte — constatarea liberă. */
  scope: 'tooth' | 'study' | 'any';
  graded?: boolean;
  chart?: string;
  patient: string;
  on: Array<'2d' | '3d'>;
  urgent?: boolean;
  /** Culoarea dintelui: patologie roșu, tratament violet, anatomie neutru, lipsă ×. */
  nature: FindingNature;
  attributes?: FindingAttributeMeta[];
}

export type FindingNature = 'pathology' | 'treatment' | 'anatomy' | 'missing';

export interface FindingAttributeMeta {
  key: string;
  label: string;
  multi: boolean;
  options: Array<{ key: string; label: string }>;
}

export interface ImagingCatalog {
  categories: Array<{ key: string; label: string }>;
  findings: FindingMeta[];
  kinds: Array<{ key: StudyKind; label: string }>;
  severities: Array<{ key: FindingSeverity; label: string }>;
}

export interface MultipartStart {
  name: string;
  path: string;
  uploadId: string;
  partSize: number;
  partCount: number;
  headers: Record<string, string>;
  ttl: number;
}

export interface SignedUpload {
  name: string;
  url: string;
  path: string;
  headers: Record<string, string>;
  ttl: number;
}

export interface StudyShareRow {
  id: string;
  label: string;
  expiresAt: string;
  revokedAt: string | null;
  openedCount: number;
  lastOpenedAt: string | null;
  createdBy: string | null;
  createdAt: string;
  /** „Trimite unui medic": adresa, comentariul, codul și încercările greșite. */
  recipientEmail: string | null;
  comment: string | null;
  withCode: boolean;
  /** Deschide toate studiile pacientului. */
  patientWide?: boolean;
  /** Când a plecat invitația pe email. */
  emailedAt?: string | null;
  failedAttempts: number;
  lockedAt: string | null;
  active: boolean;
}

export interface ImagingPatientList {
  total: number;
  patients: Array<{
    id: string;
    name: string;
    /** ID-ul pacientului din fișă. */
    recordNo: number | null;
    dob: string | null;
    doctorName: string | null;
    kinds: StudyKind[];
    lastStudyDate: string | null;
    lastStudyId: string | null;
    studies: number;
    photos: number;
    documents: number;
    activeShares: number;
    modifiedAt: string | null;
    previewUrl: string | null;
  }>;
}

/** Filtrele listei de pacienți a imagisticii. */
export interface PatientListParams {
  search?: string;
  filter?: 'all' | 'shared';
  age?: 'adults' | 'children';
  modified?: 'today' | 'week' | 'range';
  from?: string;
  to?: string;
  /** Modalitățile, despărțite prin virgulă. */
  kinds?: string;
  sort?: 'name' | 'modified';
  dir?: 'asc' | 'desc';
  offset?: number;
}

export interface ImagingPatientOverview {
  patientName: string | null;
  /** Descrierea cazului clinic, cu formatare. */
  caseDescription: { text: string; updatedBy: string | null; updatedAt: string } | null;
  /** Documentele dosarului cu fișier, cele mai noi 8, și câte sunt în total. */
  documents: {
    total: number;
    items: Array<{
      id: string;
      name: string;
      filePath: string;
      category: string | null;
      docDate: string | null;
    }>;
  };
  /**
   * Rapoartele: fiecare versiune a raportului unui studiu, cea mai nouă întâi,
   * și planurile de pe scenă (implant, ortodonție, model 3D).
   */
  reports: Array<{
    studyId: string;
    type: string;
    kind: StudyKind;
    studyDate: string;
    version: number | null;
    status: 'current' | 'archived' | 'draft' | 'plan';
    signedAt: string | null;
    signedName: string | null;
    createdAt: string;
  }>;
  /** `added`: adăugat în profil, deci se poate scoate de aici. */
  doctors: Array<{ id: string; name: string; main: boolean; added: boolean }>;
  shares: Array<{
    id: string;
    label: string;
    recipientEmail: string | null;
    expiresAt: string;
    withCode: boolean;
    /** Linkul deschide toate studiile pacientului. */
    patientWide?: boolean;
    studyId: string;
    kind: StudyKind;
    studyDate: string;
  }>;
}

/** O patologie pe dinte fără procedură în plan pe dintele acela. */
/** Ce a hotărât clinica pentru imagistică, citit de vizualizator. */
export interface ImagingPolicy {
  implantPlanning: boolean;
  /** Cum se scriu numerele dinților; baza rămâne în FDI. */
  notation?: 'fdi' | 'universal' | 'palmer';
  /** Cum iese foaia de tipărit implicit; cheile lipsă rămân ale aplicației. */
  sheet?: Partial<{
    color: boolean;
    invert: boolean;
    chartUpper: boolean;
    chartLower: boolean;
    conditions: boolean;
    slices: boolean;
    comments: boolean;
    studyMeta: boolean;
    nonDental: boolean;
    audience: 'full' | 'patient';
    language: 'ro' | 'ru';
  }>;
}

export interface UntreatedFinding {
  studyId: string;
  studyDate: string;
  kind: StudyKind;
  code: string;
  label: string;
  tooth: number;
  severity: FindingSeverity | null;
  urgent: boolean;
}

export interface Huddle {
  date: string;
  mine: boolean;
  /** Câte programări are ziua, cu filtrele date. */
  appointments: number;
  patients: Array<{
    appointmentId: string;
    patientId: string;
    patientName: string;
    time: string;
    status: string;
    doctorId: string | null;
    doctorName: string | null;
    findings: UntreatedFinding[];
  }>;
}

export interface SharedStudy {
  label: string;
  expiresAt: string;
  /** Mesajul medicului care a trimis, pe un link cu cod. */
  comment: string | null;
  /** Studiul deschis acum. */
  studyId?: string;
  /** Pe un link al pacientului întreg: studiile lui, cel mai nou întâi. */
  studies?: Array<{
    id: string;
    kind: StudyKind;
    kindLabel: string;
    studyDate: string;
    signed: boolean;
  }> | null;
  study: {
    kind: StudyKind;
    kindLabel: string;
    studyDate: string;
    device: string | null;
    patientName: string | null;
    note: string | null;
  };
  series: Array<{
    id: string;
    description: string | null;
    slices: number;
    volume: {
      width: number | null;
      height: number | null;
      depth: number | null;
      windowCenter: number | null;
      windowWidth: number | null;
    };
    url: string;
    /** Volumul întreg, cu linkurile semnate; null pe o serie de o felie. */
    manifest: VolumeManifestView | null;
  }>;
  files: Array<{
    id: string;
    name: string;
    bytes: number;
    viewable: boolean;
    url: string;
    /** PNG-ul de vizualizare al unui TIFF; `url` rămâne originalul, pentru descărcare. */
    viewUrl: string | null;
  }>;
  report: {
    version: number;
    summary: string | null;
    recommendation: string | null;
    findings: Array<ReportSnapshotItem & { nature?: string }>;
    signedAt: string | null;
    signedName: string | null;
    images: Array<{ label: string; url: string }>;
  } | null;
}

export interface ImagingStats {
  from: string;
  to: string;
  byKind: Array<{ kind: StudyKind; label: string; studies: number; failed: number; bytes: number }>;
  /** Cât ocupă toate studiile clinicii, șterse incluse. */
  storedBytes: number;
  byDoctor: Array<{
    doctor: string;
    studies: number;
    signed: number;
    prints?: number;
    exports?: number;
    shares?: number;
    teethApproved?: number;
    /** Mediana orelor de la primirea studiului la prima semnătură. */
    medianHoursToSign?: number | null;
  }>;
  opportunities: Array<{
    studyId: string;
    patientId: string;
    patientName: string;
    studyDate: string;
    code: string;
    label: string;
    tooth: number | null;
    urgent: boolean;
  }>;
}

/** Ce se scrie pe ecran pentru fiecare fel de studiu. */
export const STUDY_KIND_LABELS = i18nMap<StudyKind>('common', {
  intraoral: 'studyKindIntraoral',
  panoramic: 'studyKindPanoramic',
  cbct: 'studyKindCbct',
  stl: 'studyKindStl',
  photo: 'studyKindPhoto',
});

export const STUDY_STATUS_LABELS = i18nMap<StudyStatus>('common', {
  new: 'studyStatusNew',
  processing: 'studyStatusProcessing',
  ready: 'studyStatusReady',
  failed: 'studyStatusFailed',
});

export const SEVERITY_LABELS = i18nMap<FindingSeverity>('common', {
  mild: 'severityMild',
  moderate: 'severityModerate',
  severe: 'severitySevere',
});

/** Ce extensii se pot alege, pe fel de studiu — aceleași ca la API. */
export const STUDY_ACCEPT: Record<StudyKind, string> = {
  intraoral: '.dcm,.dicom,.png,.jpg,.jpeg,.tif,.tiff',
  panoramic: '.dcm,.dicom,.png,.jpg,.jpeg,.tif,.tiff',
  cbct: '.dcm,.dicom',
  stl: '.stl,.ply,.obj',
  photo: '.png,.jpg,.jpeg,.tif,.tiff',
};

/** Plafoanele încărcării, în octeți — aceleași pe care le aplică semnarea. */
export interface UploadLimits {
  maxFileBytes: number;
  maxStudyBytes: number;
}

export const imagingApi = {
  catalog: () => api.get<ImagingCatalog>('/imaging/catalog'),

  /** Ce unelte a pornit clinica: planificarea implantului se poate opri din Setări. */
  policy: () => api.get<ImagingPolicy>('/imaging/policy'),

  /** Citite înainte de a deschide studiul, ca un refuz să nu lase un studiu gol. */
  uploadLimits: () => api.get<UploadLimits>('/imaging/upload-limits'),

  forPatient: (patientId: string) => api.get<StudySummary[]>(`/imaging/patients/${patientId}`),

  /** Ce are clinica de citit, de reluat și de așteptat. */
  worklist: (filters: { search?: string; kind?: StudyKind; doctorId?: string } = {}) =>
    api.get<{
      failed: Array<StudySummary & { patientName?: string | null }>;
      awaiting: Array<StudySummary & { patientName?: string | null }>;
      processing: Array<StudySummary & { patientName?: string | null }>;
      /** Câte are fiecare listă cu filtrele date; rândurile sunt cele mai noi 100. */
      totals: { failed: number; awaiting: number; processing: number };
      /** Felurile și medicii din toată lista de lucru, pentru filtre. */
      facets: { kinds: StudyKind[]; doctors: Array<{ id: string; name: string }> };
    }>('/imaging/worklist', { query: filters }),

  /** Studiile făcute în timpul unei vizite. */
  forVisit: (visitId: string) => api.get<StudySummary[]>(`/imaging/visits/${visitId}`),

  detail: (id: string) => api.get<StudyDetail>(`/imaging/${id}`),

  create: (
    patientId: string,
    body: {
      kind: StudyKind;
      studyDate?: string;
      device?: string | null;
      note?: string | null;
      visitId?: string | null;
      teeth?: number[];
    },
  ) => api.post<StudyDetail>(`/imaging/patients/${patientId}`, body),

  update: (
    id: string,
    body: {
      studyDate?: string;
      device?: string | null;
      note?: string | null;
      visitId?: string | null;
      /** Înlocuiesc lista de dinți scrisă pe studiu. */
      teeth?: number[];
    },
  ) => api.patch<StudyDetail>(`/imaging/${id}`, body),

  /** Cere URL-uri semnate pentru o rundă de fișiere. */
  signUploads: (id: string, files: Array<{ name: string; contentType: string; bytes: number }>) =>
    api.post<{ uploads: SignedUpload[] }>(`/imaging/${id}/uploads`, { files }),

  /** Încărcarea în bucăți a unui fișier mare — vezi {@link uploadInParts}. */
  startMultipart: (id: string, file: { name: string; contentType: string; bytes: number }) =>
    api.post<MultipartStart>(`/imaging/${id}/uploads/multipart`, file),
  signParts: (id: string, body: { path: string; uploadId: string; parts: number[] }) =>
    api.post<{ parts: Array<{ partNumber: number; url: string }> }>(
      `/imaging/${id}/uploads/multipart/parts`,
      body,
    ),
  completeMultipart: (
    id: string,
    body: { path: string; uploadId: string; parts: Array<{ partNumber: number; etag: string }> },
  ) => api.post<{ path: string }>(`/imaging/${id}/uploads/multipart/complete`, body),
  abortMultipart: (id: string, body: { path: string; uploadId: string }) =>
    api.post<{ ok: true }>(`/imaging/${id}/uploads/multipart/abort`, body),

  registerFiles: (
    id: string,
    files: Array<{ name: string; contentType: string; bytes: number; path: string }>,
    more = false,
  ) => api.post<StudyDetail>(`/imaging/${id}/files`, { files, more }),

  ingest: (id: string) => api.post<StudyDetail>(`/imaging/${id}/ingest`),

  /**
   * `url` e originalul; `viewUrl`, PNG-ul de vizualizare al unui TIFF, când există.
   * `download` scrie descărcarea în jurnalul de acces al studiului.
   */
  fileUrl: (id: string, fileId: string, purpose?: 'view' | 'download') =>
    api.get<{ url: string; name: string; viewUrl: string | null }>(
      `/imaging/${id}/files/${fileId}/url`,
      purpose ? { query: { purpose } } : undefined,
    ),

  /** Cine a deschis, descărcat, tipărit, exportat sau partajat studiul. */
  accessLog: (id: string) =>
    api.get<
      Array<{
        id: string;
        at: string;
        actorName: string | null;
        action: string;
        summary: string | null;
      }>
    >(`/imaging/${id}/access`),

  /** O tipăritură sau un export făcut în browser, pentru jurnalul de acces. */
  recordAccess: (id: string, body: { what: 'print' | 'export'; detail?: string | null }) =>
    api.post<void>(`/imaging/${id}/access`, body),

  /** Scoate un fișier; dacă era o felie, volumul se reface din ce rămâne. */
  removeFile: (id: string, fileId: string) =>
    api.delete<StudyDetail>(`/imaging/${id}/files/${fileId}`),

  volumeUrl: (id: string, seriesId: string) =>
    api.get<{ url: string }>(`/imaging/${id}/series/${seriesId}/volume`),

  /** Volumul întreg pe 16 biți: manifestul, cu un link semnat pe fiecare bucată. */
  volumeManifest: (id: string, seriesId: string) =>
    api.get<VolumeManifestView>(`/imaging/${id}/series/${seriesId}/manifest`),

  /** Scena studiului: curbă, canal, implant, măsurători — în milimetri. */
  objects: (id: string) => api.get<SceneObject[]>(`/imaging/${id}/objects`),

  createObject: (id: string, body: SceneObjectInput) =>
    api.post<SceneObject>(`/imaging/${id}/objects`, body),

  /** Lungimile și unghiurile de pe felii, mutate pe scenă într-o tranzacție. */
  adoptAnnotations: (
    id: string,
    moves: Array<{
      annotationId: string;
      geometry: Record<string, unknown>;
      props: Record<string, unknown>;
    }>,
  ) => api.post<SceneObject[]>(`/imaging/${id}/objects/from-annotations`, { moves }),

  updateObject: (id: string, objectId: string, body: Partial<Omit<SceneObjectInput, 'type'>>) =>
    api.patch<SceneObject>(`/imaging/${id}/objects/${objectId}`, body),

  removeObject: (id: string, objectId: string) =>
    api.delete<void>(`/imaging/${id}/objects/${objectId}`),

  addFinding: (
    id: string,
    body: {
      code: string;
      tooth?: number | null;
      severity?: FindingSeverity | null;
      note?: string | null;
      attributes?: FindingAttributes;
      region?: FindingRegion | null;
    },
  ) => api.post<StudyDetail>(`/imaging/${id}/findings`, body),

  updateFinding: (
    id: string,
    findingId: string,
    body: {
      code?: string;
      severity?: FindingSeverity | null;
      note?: string | null;
      attributes?: FindingAttributes;
      region?: FindingRegion | null;
    },
  ) => api.patch<StudyDetail>(`/imaging/${id}/findings/${findingId}`, body),

  /** Comentariul pe un dinte sau aprobarea lui. */
  reviewTooth: (
    id: string,
    tooth: number,
    body: {
      comment?: string | null;
      approved?: boolean;
      excluded?: boolean;
      slices?: ToothSliceRef[] | null;
      boneLevels?: BoneLevel[] | null;
    },
  ) => api.put<StudyDetail>(`/imaging/${id}/teeth/${tooth}/review`, body),

  /** Dinții de pe un film al studiului; o dublură cu alt film se refuză. */
  setSourceTeeth: (id: string, body: { fileId?: string; seriesId?: string; teeth: number[] }) =>
    api.put<StudyDetail>(`/imaging/${id}/source-teeth`, body),

  /** Regiunea raportului: dinții puși în raport sau scoși, deodată. */
  setToothRegion: (id: string, teeth: number[], included: boolean) =>
    api.post<StudyDetail>(`/imaging/${id}/teeth/region`, { teeth, included }),

  /** „Aprobă tot": dinții din raport, deodată. */
  approveTeeth: (id: string, teeth: number[]) =>
    api.post<StudyDetail>(`/imaging/${id}/teeth/approve`, { teeth }),

  removeFinding: (id: string, findingId: string) =>
    api.delete<StudyDetail>(`/imaging/${id}/findings/${findingId}`),

  /**
   * Atașează la raport felia la care se uită medicul.
   *
   * `multipart`, ca încărcarea de documente: imaginea are zeci de kilobaiți.
   * Legenda și mărimea merg în același formular, fiindcă fără ele o imagine pe
   * un raport medical e o ilustrație, nu o dovadă.
   */
  attachReportImage: (
    id: string,
    blob: Blob,
    meta: { label: string; width: number; height: number; tooth?: number },
  ) => {
    const form = new FormData();
    form.append('file', blob, 'vedere.png');
    form.append('label', meta.label);
    form.append('width', String(meta.width));
    form.append('height', String(meta.height));
    if (meta.tooth) form.append('tooth', String(meta.tooth));
    return api.post<StudyDetail>(`/imaging/${id}/report/images`, form);
  },

  removeReportImage: (id: string, imageId: string) =>
    api.delete<StudyDetail>(`/imaging/${id}/report/images/${imageId}`),

  reportImageUrl: (id: string, imageId: string) =>
    api.get<{ url: string }>(`/imaging/${id}/report/images/${imageId}/url`),

  /** Antetul clinicii și semnatarul versiunii, pentru foaia tipărită. */
  printContext: (id: string, version: number | null) =>
    api.get<PrintContext>(
      `/imaging/${id}/print-context${version === null ? '' : `?version=${version}`}`,
    ),

  /** Arhivează raportul semnat în dosarul pacientului, ca HTML autonom. */
  archiveReport: (id: string, version: number, html: string, name: string) => {
    const form = new FormData();
    form.append('file', new Blob([html], { type: 'text/html' }), name);
    return api.post<StudyDetail>(`/imaging/${id}/report/${version}/archive`, form);
  },

  saveAnnotations: (id: string, annotations: StudyAnnotation[]) =>
    api.post<StudyDetail>(`/imaging/${id}/annotations`, {
      // Serverul pune el `createdBy` și `createdAt`: cine a măsurat și când nu
      // se iau de la client.
      annotations: annotations.map((a) => ({
        id: a.id,
        plane: a.plane,
        index: a.index,
        kind: a.kind,
        points: a.points,
        value: a.value,
        text: a.text,
        // Fără el, o distanță pe un model 3D pleca fără fișierul ei, iar
        // serverul o refuza — corect — ca pe una care nu arată spre nimic.
        // Pe o imagine pleacă fișierul, pe o felie seria: altfel o lungime de
        // pe o retroalveolară se desena pe toate imaginile studiului.
        ...(a.plane === 'model'
          ? { fileId: a.fileId ?? null }
          : a.plane === 'image'
            ? a.fileId
              ? { fileId: a.fileId }
              : {}
            : a.seriesId
              ? { seriesId: a.seriesId }
              : {}),
      })),
    }),

  applyToChart: (id: string, chartName?: string) =>
    api.post<{ applied: number }>(`/imaging/${id}/apply-to-chart`, chartName ? { chartName } : {}),

  saveReport: (id: string, body: ReportBody) =>
    api.post<StudyDetail>(`/imaging/${id}/report`, body),

  signReport: (id: string, body: ReportBody) =>
    api.post<StudyDetail>(`/imaging/${id}/report/sign`, body),

  remove: (id: string, reason?: string) =>
    api.delete<void>(`/imaging/${id}`, { query: { reason } }),

  shares: (id: string) => api.get<StudyShareRow[]>(`/imaging/${id}/shares`),

  /**
   * Face un link de partajare. Jetonul vine **o singură dată** — pe urmă în
   * bază rămâne doar amprenta lui, ca la o parolă.
   */
  createShare: (
    id: string,
    body: {
      label: string;
      days?: number;
      withCode?: boolean;
      email?: string;
      comment?: string | null;
      declareProfessional?: boolean;
      declareConsent?: boolean;
      patientWide?: boolean;
      sendEmail?: boolean;
    },
  ) =>
    api.post<{
      id: string;
      label: string;
      expiresAt: string;
      token: string;
      code: string | null;
      patientWide?: boolean;
      emailed?: boolean;
      emailNote?: string | null;
    }>(`/imaging/${id}/shares`, body),

  revokeShare: (id: string, shareId: string) =>
    api.delete<StudyShareRow[]>(`/imaging/${id}/shares/${shareId}`),

  /** Ce vede cineva din afara clinicii. Fără cont, cu jetonul din link. */
  openShared: (token: string, study?: string) =>
    api.get<SharedStudy>(`/imaging/shared/${encodeURIComponent(token)}`, {
      query: study ? { study } : undefined,
    }),

  /** Un link trimis unui medic: codul de acces merge în corp, nu în adresă. */
  openSharedWithCode: (token: string, code: string, studyId?: string) =>
    api.post<SharedStudy>(
      `/imaging/shared/${encodeURIComponent(token)}`,
      studyId ? { code, studyId } : { code },
    ),

  /** Pacienții cu studii, cu modalitățile și trimiterile active. */
  patientList: (params: PatientListParams) =>
    api.get<ImagingPatientList>('/imaging/patient-list', {
      query: Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== 0),
      ),
    }),

  /** Medicii curanți și linkurile active ale pacientului, pentru cardul lui. */
  patientOverview: (patientId: string) =>
    api.get<ImagingPatientOverview>(`/imaging/patients/${patientId}/overview`),

  /** Adaugă sau scoate medici curanți. */
  setTreatingDoctors: (patientId: string, body: { add?: string[]; remove?: string[] }) =>
    api.put<ImagingPatientOverview>(`/imaging/patients/${patientId}/treating-doctors`, body),

  /** Descrierea cazului clinic; null o șterge. */
  setCaseDescription: (patientId: string, text: string | null) =>
    api.put<ImagingPatientOverview>(`/imaging/patients/${patientId}/case`, { text }),

  /** Ședința de dimineață: pacienții zilei cu constatări netratate. */
  huddle: (params: { date?: string; mine?: boolean }) =>
    api.get<Huddle>('/imaging/huddle', {
      query: {
        ...(params.date ? { date: params.date } : {}),
        ...(params.mine ? { mine: 'true' } : {}),
      },
    }),

  /** Constatările netratate ale unui pacient, pentru panoul programării. */
  untreated: (patientId: string) =>
    api.get<UntreatedFinding[]>(`/imaging/patients/${patientId}/untreated`),

  stats: (params?: { from?: string; to?: string }) =>
    api.get<ImagingStats>('/imaging/stats', { query: params }),
};

/**
 * Urcă un fișier direct în depozit, cu URL-ul semnat de API.
 *
 * `fetch` cu `PUT`, nu clientul obișnuit: ținta nu e API-ul nostru, deci nu
 * poartă nici token, nici prefix de versiune. Antetele vin exact cum le-a
 * semnat serverul — un `Content-Type` schimbat aici face semnătura invalidă și
 * răspunsul 403, care arată ca o problemă de drepturi și e o nepotrivire de
 * antet.
 */
/** Cât poate sta un transfer fără să mai treacă vreun octet, înainte de a fi reluat. */
export const UPLOAD_STALL_MS = 90_000;

/**
 * Urcă un fișier la URL-ul semnat, cu progres și cu un paznic pentru blocaje.
 *
 * Era un `fetch`, iar `fetch` nu spune cât a urcat. Pentru un CBCT exportat
 * ca un singur fișier de câteva sute de MB, ecranul stătea pe „0 din 1" și cu
 * bara goală până la ultimul octet — un transfer lent și unul înghețat arătau
 * la fel, iar unul înghețat atârna la nesfârșit, fără nicio eroare. Acum:
 *
 * - `onProgress` primește octeții urcați, pe măsură ce trec;
 * - dacă {@link UPLOAD_STALL_MS} nu mai trece nimic, încercarea se oprește și
 *   se reia; după a treia, eroarea spune ce s-a întâmplat.
 *
 * Trei încercări, cu pauză între ele: rețeaua unui cabinet cade pentru câteva
 * secunde. URL-ul e valabil o oră, deci reîncercarea folosește același.
 */
export async function putToStorage(
  upload: SignedUpload,
  file: File,
  signal?: AbortSignal,
  onProgress?: (loaded: number) => void,
): Promise<void> {
  let last: unknown = null;
  for (let attempt = 1; attempt <= 3; attempt += 1) {
    try {
      await putOnce(upload, file, file.name, signal, onProgress);
      return;
    } catch (e) {
      // 4xx nu se repară prin repetare: e o semnătură greșită sau un antet
      // care nu se potrivește, iar repetarea ar ascunde cauza.
      if (e instanceof Error && e.message.includes('refuzată')) throw e;
      // Oprită de om: nu se reîncearcă.
      if (signal?.aborted) throw e;
      last = e;
    }
    onProgress?.(0);
    if (attempt < 3) await new Promise((r) => setTimeout(r, attempt * 700));
  }
  throw last instanceof Error ? last : new Error(`Încărcarea „${file.name}" a eșuat`);
}

/**
 * O singură încercare: un `PUT` prin XHR, singurul care raportează progresul
 * urcării. Întoarce ETag-ul răspunsului — o parte a unei încărcări în bucăți
 * se închide cu el.
 */
function putOnce(
  upload: Pick<SignedUpload, 'url' | 'headers'>,
  file: Blob,
  name: string,
  signal: AbortSignal | undefined,
  onProgress: ((loaded: number) => void) | undefined,
): Promise<string | null> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let lastMove = Date.now();
    let stalled = false;

    const watchdog = setInterval(() => {
      if (Date.now() - lastMove > UPLOAD_STALL_MS) {
        stalled = true;
        xhr.abort();
      }
    }, 5_000);
    const onAbort = () => xhr.abort();
    const finish = () => {
      clearInterval(watchdog);
      signal?.removeEventListener('abort', onAbort);
    };

    xhr.open('PUT', upload.url);
    for (const [name, value] of Object.entries(upload.headers)) {
      xhr.setRequestHeader(name, value);
    }
    xhr.upload.onprogress = (e) => {
      lastMove = Date.now();
      onProgress?.(e.loaded);
    };
    xhr.onload = () => {
      finish();
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(file.size);
        resolve(xhr.getResponseHeader('ETag'));
      } else if (xhr.status >= 400 && xhr.status < 500) {
        reject(new Error(`Încărcarea „${name}" a fost refuzată (${xhr.status})`));
      } else {
        reject(new Error(`Încărcarea „${name}" a eșuat (${xhr.status})`));
      }
    };
    xhr.onerror = () => {
      finish();
      reject(new Error(`Încărcarea „${name}" s-a întrerupt. Verifică legătura la internet.`));
    };
    xhr.onabort = () => {
      finish();
      reject(
        stalled
          ? new Error(
              `Încărcarea „${name}" s-a oprit: nu a mai trecut nimic în ` +
                `${UPLOAD_STALL_MS / 1000} de secunde. Verifică legătura la internet și reia.`,
            )
          : new DOMException('Oprită', 'AbortError'),
      );
    };

    if (signal?.aborted) {
      finish();
      reject(new DOMException('Oprită', 'AbortError'));
      return;
    }
    signal?.addEventListener('abort', onAbort);
    xhr.send(file);
  });
}

/** De la ce mărime un fișier urcă în bucăți, nu într-un singur `PUT`. */
export const MULTIPART_FROM = 64 * 1024 * 1024;
/** Câte părți urcă deodată. */
const PART_PARALLEL = 4;
/** Câte link-uri de părți se cer o dată: un link trăiește o oră. */
const PART_SIGN_BATCH = 32;

/**
 * Urcă un fișier mare în bucăți și întoarce cheia lui din depozit.
 *
 * Un CBCT de 450 MB într-un singur `PUT` se pierdea întreg la prima cădere a
 * rețelei și se relua de la zero. Aici urcă în părți de 16 MB, câte patru
 * deodată: o parte picată se trimite din nou singură — cu aceleași trei
 * încercări și același paznic de blocaj ca un fișier întreg —, iar ce a urcat
 * rămâne urcat. Link-urile părților se cer pe măsură ce e nevoie de ele, fiindcă
 * fiecare trăiește o oră.
 *
 * Oprită sau picată, încărcarea se anulează în depozit: părțile deja urcate nu
 * rămân în bucket, unde nu le-ar vedea nimeni și le-ar plăti clinica.
 */
export async function uploadInParts(
  studyId: string,
  file: File,
  contentType: string,
  signal?: AbortSignal,
  onProgress?: (loaded: number) => void,
): Promise<string> {
  const started = await imagingApi.startMultipart(studyId, {
    name: file.name,
    contentType,
    bytes: file.size,
  });
  const ref = { path: started.path, uploadId: started.uploadId };
  const loaded = new Map<number, number>();
  const report = () => onProgress?.([...loaded.values()].reduce((a, b) => a + b, 0));

  const signed = new Map<number, Promise<Map<number, string>>>();
  const urlOf = async (part: number): Promise<string> => {
    const batch = Math.floor((part - 1) / PART_SIGN_BATCH);
    let links = signed.get(batch);
    if (!links) {
      const first = batch * PART_SIGN_BATCH + 1;
      const last = Math.min(started.partCount, first + PART_SIGN_BATCH - 1);
      const parts = Array.from({ length: last - first + 1 }, (_, i) => first + i);
      links = imagingApi
        .signParts(studyId, { ...ref, parts })
        .then((r) => new Map(r.parts.map((p) => [p.partNumber, p.url])));
      signed.set(batch, links);
    }
    const url = (await links).get(part);
    if (!url) throw new Error(`Încărcarea „${file.name}": lipsește link-ul părții ${part}`);
    return url;
  };

  const sendPart = async (part: number): Promise<{ partNumber: number; etag: string }> => {
    const from = (part - 1) * started.partSize;
    const blob = file.slice(from, Math.min(file.size, from + started.partSize));
    const url = await urlOf(part);
    let last: unknown = null;
    for (let attempt = 1; attempt <= 3; attempt += 1) {
      try {
        const etag = await putOnce(
          { url, headers: started.headers },
          blob,
          file.name,
          signal,
          (n) => {
            loaded.set(part, n);
            report();
          },
        );
        if (!etag)
          throw new Error(`Încărcarea „${file.name}": depozitul nu a confirmat partea ${part}`);
        loaded.set(part, blob.size);
        report();
        return { partNumber: part, etag };
      } catch (e) {
        if (e instanceof Error && e.message.includes('refuzată')) throw e;
        if (signal?.aborted) throw e;
        last = e;
      }
      loaded.set(part, 0);
      report();
      if (attempt < 3) await new Promise((r) => setTimeout(r, attempt * 700));
    }
    throw last instanceof Error ? last : new Error(`Încărcarea „${file.name}" a eșuat`);
  };

  try {
    const done: Array<{ partNumber: number; etag: string }> = [];
    let next = 1;
    const worker = async () => {
      while (next <= started.partCount) {
        const part = next;
        next += 1;
        done.push(await sendPart(part));
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(PART_PARALLEL, started.partCount) }, () => worker()),
    );
    await imagingApi.completeMultipart(studyId, { ...ref, parts: done });
    return started.path;
  } catch (e) {
    void imagingApi.abortMultipart(studyId, ref).catch(() => undefined);
    throw e;
  }
}
